import { BCMSDate, BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account } from "model";
import TASPositivePayCheckForm from "../TASPositivePayCheckForm";

/**
 * @class TASPositivePayCheckFormUI
 * @classdesc Class definition for controlling a check
 * form which will then be transformed into a positive pay check.
 * 
 * @property {STRING} _id - The id of the check form.
 * @property {TASPositivePayCheckForm} _checkForm - The check form.
 * @property {BOOLEAN} _isEditing - The is editing ui.
 * @property {OBJECT} _account - The account ui.
 * @property {OBJECT} _issuedDate - The issued date ui.
 * @property {OBJECT} _type - The type ui.
 * @property {OBJECT} _mobile - The mobile ui.
 */
export default class TASPositivePayCheckFormUI {
    constructor(record) {
        this.id = record.id;
        this.checkForm = record.checkForm;
        this.isEditing = record.isEditing;
        this.account = record.account;
        this.issuedDate = record.issuedDate;
        this.type = record.type;
        this.mobile = record.mobile;
    }

    /**
     * @name id
     * @instance
     * @property set {STRING} - sets the id
     * @memberof TASPositivePayCheckFormUI
     */
    set id(value) { this._id = value; }

    /**
     * @name checkForm
     * @instance
     * @property set {OBJECT} - sets the check form
     * @memberof TASPositivePayCheckFormUI
     */
    set checkForm(value) { this._checkForm = value; }

    /**
     * @name isEditing
     * @instance
     * @property set {BOOLEAN} - sets the is editing ui
     * @memberof TASPositivePayCheckFormUI
     */
    set isEditing(value) { this._isEditing = value; }

    /**
     * @name account
     * @instance
     * @property set {OBJECT} - sets the account ui
     * @memberof TASPositivePayCheckFormUI
     */
    set account(value) { this._account = value; }

    /**
     * @name issuedDate
     * @instance
     * @property set {OBJECT} - sets the issued date ui
     * @memberof TASPositivePayCheckFormUI
     */
    set issuedDate(value) { this._issuedDate = value; }

    /**
     * @name type
     * @instance
     * @property set {STRING} - sets the type
     * @memberof TASPositivePayCheckFormUI
     */
    set type(value) { this._type = value; }

    /**
     * @name mobile
     * @instance
     * @property set {BOOLEAN} - sets the mobile ui
     * @memberof TASPositivePayCheckFormUI
     */
    set mobile(value) { this._mobile = value; }

    /**
     * @name id
     * @instance
     * @property get {STRING} - returns the id
     * @memberof TASPositivePayCheckFormUI
     */
    get id() { return this._id; }

    /**
     * @name checkForm
     * @instance
     * @property get {OBJECT} - returns the check form
     * @memberof TASPositivePayCheckFormUI
     */
    get checkForm() { return this._checkForm; }

    /**
     * @name isEditing
     * @instance
     * @property get {BOOLEAN} - returns the is editing ui
     * @memberof TASPositivePayCheckFormUI
     */
    get isEditing() { return this._isEditing; }

    /**
     * @name account
     * @instance
     * @property get {OBJECT} - returns the account ui
     * @memberof TASPositivePayCheckFormUI
     */
    get account() { return this._account; }

    /**
     * @name issuedDate
     * @instance
     * @property get {OBJECT} - returns the issued date ui
     * @memberof TASPositivePayCheckFormUI
     */
    get issuedDate() { return this._issuedDate; }

    /**
     * @name type
     * @instance
     * @property get {STRING} - returns the type
     * @memberof TASPositivePayCheckFormUI
     */
    get type() { return this._type; }

    /**
     * @name mobile
     * @instance
     * @property get {BOOLEAN} - returns the mobile ui
     * @memberof TASPositivePayCheckFormUI
     */
    get mobile() { return this._mobile; }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayCheckFormUI
     */
    get record() {
        return {
            id: this.id,
            checkForm: this.checkForm,
            isEditing: this.isEditing,
            account: this.account,
            issuedDate: this.issuedDate,
            type: this.type,
            mobile: this.mobile
        };
    }

    /**
     * @function reset
     * @instance
     * @property {VOID} - No parameters.
     * @description Returns an instance of the class with default values.
     * @memberof TASPositivePayCheckFormUI
     */
    reset() {
        const defaultVals = TASPositivePayCheckFormUI.defaultVals();
        return TASPositivePayCheckFormUI.init(defaultVals);
    }

    /** 
     * @function defaultVals   
     * @static
     * @property {VOID} - No parameters
     * @description Returns the default values of the class.
     * @memberof TASPositivePayCheckFormUI 
     */
    static defaultVals() {
        return { 
            id: "0",
            checkForm: TASPositivePayCheckForm.init(),
            isEditing: false,
            account: { isActive: false },
            issuedDate: { isActive: false },
            type: { isActive: false },
            mobile: { isActive: false }
        };
    }
    
    /** 
     * @function init   
     * @static
     * @property {OBJECT} - An object to initialize an instance of the class.
     * @description Returns an instance of the class.
     * @memberof TASPositivePayCheckFormUI 
     */
    static init(record) {
        const defaultVals = TASPositivePayCheckFormUI.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayCheckFormUI(vals);
    }
}